import { useState, useCallback } from 'react';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

interface UseFullscreenReturn {
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  enterFullscreen: () => void;
  exitFullscreen: () => void;
  autoEnterOnMessage: () => void;
}

interface UseFullscreenOptions {
  initialState?: boolean;
  autoExpandOnMessage?: boolean;
}

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

function useFullscreen(
  options: UseFullscreenOptions = {},
): UseFullscreenReturn {
  const { initialState = false, autoExpandOnMessage = false } = options;
  const [isFullscreen, setIsFullscreen] = useState<boolean>(initialState);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const enterFullscreen = useCallback(() => {
    setIsFullscreen(true);
  }, []);

  const exitFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const autoEnterOnMessage = useCallback(() => {
    if (autoExpandOnMessage && !isFullscreen) {
      setIsFullscreen(true);
    }
  }, [autoExpandOnMessage, isFullscreen]);

  return {
    isFullscreen,
    toggleFullscreen,
    enterFullscreen,
    exitFullscreen,
    autoEnterOnMessage,
  };
}

export default useFullscreen;
